import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Eye, Search, UserCheck, UserX, ShoppingBag, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomers = () => {
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (searchQuery = '') => {
    setLoading(true);
    try {
      const url = searchQuery 
        ? `${API}/admin/customers?search=${encodeURIComponent(searchQuery)}`
        : `${API}/admin/customers`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  const viewCustomerDetails = async (customerId) => {
    setLoadingDetails(true);
    setSelectedCustomer(customerId);
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, {
        headers: getAuthHeader()
      });
      setCustomerDetails(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
      setSelectedCustomer(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleCustomerStatus = async (customerId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/customers/${customerId}/status?is_active=${!currentStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Customer ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchCustomers(search);
      if (customerDetails?.id === customerId) {
        setCustomerDetails(prev => ({ ...prev, is_active: !currentStatus }));
      }
    } catch (error) {
      toast.error('Failed to update customer status');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Customers</h1>
          <p className="text-muted-foreground">Manage your customer base</p>
        </div>
        
        <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers..."
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
          <Button type="submit" variant="outline">
            Search
          </Button>
        </form>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Customers</p>
              <p className="text-2xl font-semibold">{customers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-sage" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-semibold">{customers.filter(c => c.is_active !== false).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-terracotta" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With Orders</p>
              <p className="text-2xl font-semibold">{customers.filter(c => c.order_count > 0).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 skeleton rounded-xl" />
          ))}
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {search ? 'No customers found matching your search' : 'No registered customers yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Orders</th>
                <th>Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer, index) => (
                <motion.tr
                  key={customer.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className={customer.is_active === false ? 'opacity-60' : ''}
                >
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <span className="text-accent font-medium">
                          {customer.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-charcoal">{customer.name}</p>
                        <p className="text-xs text-muted-foreground">{customer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <p className="text-sm">{customer.phone || '-'}</p>
                    <p className="text-xs text-muted-foreground">{customer.city || '-'}</p>
                  </td>
                  <td>
                    <span className="font-medium">{customer.order_count || 0}</span>
                  </td>
                  <td>
                    <p className="text-sm">
                      {new Date(customer.created_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </td>
                  <td>
                    <span className={`badge-handcraft ${customer.is_active !== false ? 'bg-sage/20 text-sage' : 'bg-muted text-muted-foreground'}`}>
                      {customer.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewCustomerDetails(customer.id)}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleCustomerStatus(customer.id, customer.is_active !== false)}
                        className={customer.is_active !== false ? 'text-destructive hover:text-destructive' : 'text-sage hover:text-sage'}
                        data-testid={`toggle-customer-${customer.id}`}
                      >
                        {customer.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Customer Details Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => { setSelectedCustomer(null); setCustomerDetails(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Customer Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-12 flex items-center justify-center">
              <div className="loader" />
            </div>
          ) : customerDetails && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                  <span className="text-accent text-2xl font-medium">
                    {customerDetails.name?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-xl text-charcoal">{customerDetails.name}</h3>
                    <span className={`badge-handcraft ${customerDetails.is_active !== false ? 'bg-sage/20 text-sage' : 'bg-muted text-muted-foreground'}`}>
                      {customerDetails.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {customerDetails.email}
                    </span>
                    {customerDetails.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {customerDetails.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-semibold text-charcoal">{customerDetails.total_orders || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Orders</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-semibold text-charcoal">₹{(customerDetails.total_spent || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Spent</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-semibold text-charcoal">
                    {new Date(customerDetails.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                  </p>
                  <p className="text-xs text-muted-foreground">Member Since</p>
                </div>
              </div>

              {/* Address */}
              {(customerDetails.address || customerDetails.city) && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Address</p>
                  <p className="text-charcoal">
                    {customerDetails.address && <span>{customerDetails.address}<br /></span>}
                    {customerDetails.city && <span>{customerDetails.city}</span>}
                    {customerDetails.state && <span>, {customerDetails.state}</span>}
                    {customerDetails.pincode && <span> - {customerDetails.pincode}</span>}
                  </p>
                </div>
              )}

              {/* Orders */}
              <div>
                <h4 className="font-medium text-charcoal mb-3">Recent Orders</h4>
                {customerDetails.orders?.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {customerDetails.orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">{order.order_number}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString('en-IN')} • {order.items?.length} items
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">₹{order.total?.toLocaleString()}</p>
                          <span className={`badge-handcraft text-xs ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No orders yet</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant={customerDetails.is_active !== false ? 'destructive' : 'default'}
                  onClick={() => toggleCustomerStatus(customerDetails.id, customerDetails.is_active !== false)}
                >
                  {customerDetails.is_active !== false ? (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Deactivate Customer
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Activate Customer
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
